// Motion helpers that read their curves from the theme, so JS and CSS move alike (DevRobot UI kit).

const root = () => document.documentElement;
const token = (name: string, fallback: string) => getComputedStyle(root()).getPropertyValue(name).trim() || fallback;

export const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export const ease = {
  get soft() {
    return token('--ease-soft', 'cubic-bezier(0.22, 1, 0.36, 1)');
  },
  get glide() {
    return token('--ease-glide', 'cubic-bezier(0.65, 0, 0.35, 1)');
  },
  get spring() {
    return token('--ease-spring', 'cubic-bezier(0.34, 1.3, 0.64, 1)');
  },
};

export type TransitionKind = 'route' | 'list';

/**
 * Run a synchronous DOM update inside a View Transition when the browser supports it.
 * `kind` adds `dr-vt-<kind>` to <html> while it runs, so the theme CSS can scope
 * animations: "route" (page swap) or "list" (items glide to new positions).
 * In React, wrap state updates in flushSync so the DOM is final when `update` returns.
 */
export function transition(update: () => void, { kind }: { kind?: TransitionKind } = {}): Promise<void> {
  if (!document.startViewTransition || reducedMotion()) {
    update();
    return Promise.resolve();
  }
  if (kind) root().classList.add(`dr-vt-${kind}`);
  const vt = document.startViewTransition(update);
  return vt.finished
    .catch(() => {})
    .finally(() => {
      if (kind) root().classList.remove(`dr-vt-${kind}`);
    });
}

/**
 * Slide a soft indicator ("glider") under the active item of a nav or segmented control.
 * The glider is an absolutely positioned child of the items' container.
 */
export function glide(glider: HTMLElement | null, target: Element | null | undefined, { axis = 'y', instant = false }: { axis?: 'x' | 'y'; instant?: boolean } = {}) {
  if (!glider) return;
  if (!target) {
    glider.style.opacity = '0';
    return;
  }
  const parent = (glider.offsetParent ?? glider.parentElement) as HTMLElement | null;
  if (!parent) return;
  const a = target.getBoundingClientRect();
  const b = parent.getBoundingClientRect();
  if (instant) glider.style.transition = 'none';
  if (axis === 'y') {
    glider.style.translate = `0 ${a.top - b.top + parent.scrollTop}px`;
    glider.style.height = `${a.height}px`;
  } else {
    glider.style.translate = `${a.left - b.left + parent.scrollLeft}px 0`;
    glider.style.width = `${a.width}px`;
    glider.style.height = `${a.height}px`;
  }
  glider.style.opacity = '1';
  if (instant) {
    glider.getBoundingClientRect();
    glider.style.transition = '';
  }
}
